// webapp/frontend/src/store/index.js
import { createStore } from 'vuex'
import api from '@/services/api'
import levelsModule from './modules/levels'
import snapshots from './modules/snapshots'
import projects from './modules/projects'

const MAX_RECENT_ITEMS = 20

export default createStore({
  state: {
    devItems: [],
    devItemsTotal: 0,
    devItemsLoading: false,
    devItemsError: null,
    devItemsLoadingProgress: 0,
    users: [],
    usersLoading: false,
    usersError: null,
    systems: [],
    systemsLoading: false,
    systemsError: null,
    subsystems: [],
    subsystemsLoading: false,
    subsystemsError: null,
    selectedDevItems: [],
    isLoading: false,
    error: null,
    isAuthenticated: false,
    user: null,
    recentItems: []
  },

  getters: {
    devItems: state => state.devItems,
    devItemsTotal: state => state.devItemsTotal,
    devItemsLoading: state => state.devItemsLoading,
    devItemsError: state => state.devItemsError,
    devItemsLoadingProgress: state => state.devItemsLoadingProgress,
    users: state => state.users,
    systems: state => state.systems,
    subsystems: state => state.subsystems,
    selectedDevItems: state => state.selectedDevItems
  },

  mutations: {
    SET_DEV_ITEMS(state, { items, append }) {
      if (append) {
        state.devItems = [...state.devItems, ...items]
      } else {
        state.devItems = items
      }
    },
    SET_DEV_ITEMS_TOTAL(state, total) {
      state.devItemsTotal = total
    },
    SET_DEV_ITEMS_LOADING(state, loading) {
      state.devItemsLoading = loading
    },
    SET_DEV_ITEMS_ERROR(state, error) {
      state.devItemsError = error
    },
    SET_DEV_ITEMS_LOADING_PROGRESS(state, progress) {
      state.devItemsLoadingProgress = progress
    },
    SET_USERS(state, users) {
      state.users = users
    },
    SET_USERS_LOADING(state, loading) {
      state.usersLoading = loading
    },
    SET_USERS_ERROR(state, error) {
      state.usersError = error
    },
    SET_SYSTEMS(state, systems) {
      state.systems = systems
    },
    SET_SYSTEMS_LOADING(state, loading) {
      state.systemsLoading = loading
    },
    SET_SYSTEMS_ERROR(state, error) {
      state.systemsError = error
    },
    SET_SUBSYSTEMS(state, subsystems) {
      state.subsystems = subsystems
    },
    SET_SUBSYSTEMS_LOADING(state, loading) {
      state.subsystemsLoading = loading
    },
    SET_SUBSYSTEMS_ERROR(state, error) {
      state.subsystemsError = error
    },
    SET_SELECTED_DEV_ITEMS(state, itemIds) {
      console.log('Setting selected dev items:', itemIds)
      state.selectedDevItems = itemIds
    },
    SET_LOADING(state, isLoading) {
      state.isLoading = isLoading
    },
    SET_ERROR(state, error) {
      state.error = error
    },
    setAuthenticated(state, value) {
      state.isAuthenticated = value
    },
    setUser(state, user) {
      state.user = user
    },
    SET_DEV_ITEM(state, item) {
      const index = state.devItems.findIndex(i => i.id === item.id)
      if (index !== -1) {
        // Update existing item
        state.devItems.splice(index, 1, item)
      } else {
        // Add new item
        state.devItems.push(item)
      }
    },
    ADD_RECENT_ITEM(state, { item, projectId }) {
      if (!projectId) return // Should not happen if called correctly

      const type = item.type || 'devitem'
      const normalized = { ...item, type }
      
      // Remove if already exists (match by composite id+type)
      state.recentItems = state.recentItems.filter(i => !(i.id === normalized.id && (i.type || 'devitem') === type))

      // Add to front of array
      state.recentItems.unshift(normalized)
      
      // Limit to max items
      state.recentItems = state.recentItems.slice(0, MAX_RECENT_ITEMS)
      
      // Persist to localStorage with project-specific key
      localStorage.setItem(`recentItems_${projectId}`, JSON.stringify(state.recentItems))
    },
    LOAD_RECENT_ITEMS(state, projectId) {
      if (!projectId) {
        state.recentItems = []
        return
      }
      try {
        const saved = localStorage.getItem(`recentItems_${projectId}`)
        if (saved) {
          state.recentItems = JSON.parse(saved)
        } else {
          state.recentItems = []
        }
      } catch (e) {
        console.error('Error loading recent items from localStorage:', e)
        state.recentItems = []
      }
    },
    REMOVE_RECENT_ITEM(state, { id, type = null, projectId }) {
      if (!projectId) return

      // Remove item by id (optionally restrict to type)
      state.recentItems = state.recentItems.filter(i => {
        if (type) return !(i.id === id && i.type === type)
        return i.id !== id
      })
      localStorage.setItem(`recentItems_${projectId}`, JSON.stringify(state.recentItems))
    },
    CLEAR_RECENT_ITEMS(state, projectId) {
      state.recentItems = []
      if (projectId) {
        localStorage.removeItem(`recentItems_${projectId}`)
      }
    }
  },

  actions: {
    async fetchDevItems({ commit }, { append = false } = {}) {
      try {
        commit('SET_DEV_ITEMS_LOADING', true)
        commit('SET_DEV_ITEMS_ERROR', null)

        console.log('Fetching dev items from API...')
        const data = await api.getDevItems()
        console.log('Dev items API response:', data)

        commit('SET_DEV_ITEMS', { 
          items: data.items,
          append
        })
        commit('SET_DEV_ITEMS_TOTAL', data.total)
      } catch (error) {
        console.error('Error details:', error)
        commit('SET_DEV_ITEMS_ERROR', error.message)
        throw error
      } finally {
        commit('SET_DEV_ITEMS_LOADING', false)
      }
    },
    async fetchUsers({ commit }) {
      commit('SET_USERS_LOADING', true)
      commit('SET_USERS_ERROR', null)
      try {
        const users = await api.getUsers()
        commit('SET_USERS', users)
      } catch (error) {
        commit('SET_USERS_ERROR', error)
        console.error('Error fetching users:', error)
      } finally {
        commit('SET_USERS_LOADING', false)
      }
    },
    async fetchSystems({ commit }) {
      commit('SET_SYSTEMS_LOADING', true)
      commit('SET_SYSTEMS_ERROR', null)
      try {
        const systems = await api.getSystems()
        commit('SET_SYSTEMS', systems)
      } catch (error) {
        commit('SET_SYSTEMS_ERROR', error)
        console.error('Error fetching systems:', error)
      } finally {
        commit('SET_SYSTEMS_LOADING', false)
      }
    },
    async fetchSubsystems({ commit }) {
      commit('SET_SUBSYSTEMS_LOADING', true)
      commit('SET_SUBSYSTEMS_ERROR', null)
      try {
        const subsystems = await api.getSubsystems()
        commit('SET_SUBSYSTEMS', subsystems)
      } catch (error) {
        commit('SET_SUBSYSTEMS_ERROR', error)
        console.error('Error fetching subsystems:', error)
      } finally {
        commit('SET_SUBSYSTEMS_LOADING', false)
      }
    },
    selectDevItems({ commit }, itemIds) {
      console.log('selectDevItems action called with:', itemIds)
      commit('SET_SELECTED_DEV_ITEMS', itemIds)
    },
    async loadAllDevItems({ dispatch, commit }) {
      try {
        // First load initial batch
        await dispatch('fetchDevItems')
        
        // Set progress to 100% since we load all at once now
        commit('SET_DEV_ITEMS_LOADING_PROGRESS', 100)
      } catch (error) {
        console.error('Error loading all dev items:', error)
        throw error
      } finally {
        commit('SET_DEV_ITEMS_LOADING_PROGRESS', 100)
      }
    },
    async checkAuth({ commit }) {
      try {
        const user = await api.getCurrentUser()
        commit('setUser', user)
        commit('setAuthenticated', true)
        return true
      } catch (error) {
        commit('setUser', null)
        commit('setAuthenticated', false)
        return false
      }
    },
    async loadDevItem({ commit }, { id }) {
      try {
        commit('SET_LOADING', true)
        // Simplified loading like systems - just get by ID
        const item = await api.getDevItemById(id)
        commit('SET_DEV_ITEM', item)
        return item
      } catch (error) {
        console.error('Error loading dev item:', error)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    async updateDevItem({ commit }, { id, data }) {
      try {
        commit('SET_LOADING', true)
        const item = await api.put(`/dev-items/${id}`, data)
        commit('SET_DEV_ITEM', item)
        return item
      } catch (error) {
        console.error('Error updating dev item:', error)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    async addRecentItem({ commit, state, rootGetters }, { id, name, slug, type = 'devitem' }) {
      const projectId = rootGetters['projects/currentProjectId']
      if (!projectId) return

      // Only add if not already at front of list (consider type as well)
      if (
        !state.recentItems.length ||
        state.recentItems[0].id !== id ||
        (state.recentItems[0].type || 'devitem') !== type
      ) {
        commit('ADD_RECENT_ITEM', { item: { id, name, slug, type }, projectId })
      }
    },
    loadRecentItems({ commit, rootGetters }) {
      const projectId = rootGetters['projects/currentProjectId']
      commit('LOAD_RECENT_ITEMS', projectId)
    },
    removeRecentItem({ commit, rootGetters }, payload) {
      const projectId = rootGetters['projects/currentProjectId']
      commit('REMOVE_RECENT_ITEM', { ...payload, projectId })
    },
    clearRecentItems({ commit, rootGetters }) {
      const projectId = rootGetters['projects/currentProjectId']
      commit('CLEAR_RECENT_ITEMS', projectId)
    }
  },

  modules: {
    levels: levelsModule,
    snapshots,
    projects
  }
})